import { useState, useEffect } from "react";
import { Gift, ChevronDown, ChevronUp, Coins, Trophy } from "lucide-react";
import { api } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getTier } from "@/components/uncover/tiers";
import "./trophyCase.css";

const formatCoins = (n = 0) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return n.toLocaleString("it-IT");
};

const tierOf = (coins = 0) =>
  coins >= 10000000 ? "mythic" : coins >= 5000 ? "legendary" : coins >= 1000 ? "epic" : coins >= 200 ? "rare" : "common";

function TrophyArt({ type, url, size = 54 }) {
  const [broken, setBroken] = useState(false);
  const src = url || (type ? `/images/gifts/${type}.png` : null);
  if (!src || broken) {
    return <div className="tc-art-fallback" style={{ width: size, height: size }}><Gift className="w-5 h-5" /></div>;
  }
  return <img src={src} alt="" onError={() => setBroken(true)} style={{ width: size, height: size }}
    className="tc-art" data-testid="trophy-art" />;
}

// Vetrina trofei: la collezione di gift come bacheca con luci e rarità
function TrophyShelf({ data, compact = false }) {
  const items = (data?.by_type || []).slice(0, compact ? 6 : 24);
  return (
    <div className="tc-shelf" data-testid="trophy-shelf">
      <div className="tc-shelf-glow" />
      <div className="tc-grid">
        {items.map((g, i) => {
          const cfg = getTier(tierOf((g.total_coins || 0) / Math.max(1, g.count)));
          return (
            <motion.div key={g.gift_type} className="tc-slot"
              initial={{ opacity: 0, y: 14, scale: 0.92 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
              style={{ "--tc-accent": cfg.accent, "--tc-glow": cfg.glow }}
              data-testid={`trophy-${g.gift_type}`}>
              <span className="tc-beam" />
              <span className="tc-halo" />
              <TrophyArt type={g.gift_type} url={g.gift_image} />
              <span className="tc-count">x{g.count}</span>
              <p className="tc-name">{g.gift_name}</p>
              <span className="tc-tier">{cfg.label}</span>
              <span className="tc-slot-coins" data-testid={`trophy-coins-${g.gift_type}`}>
                {formatCoins(g.total_coins || g.total_value || 0)} ADE
              </span>
              <span className="tc-base" />
            </motion.div>
          );
        })}
      </div>
      {items.length === 0 && (
        <div className="tc-empty">
          <Trophy className="w-6 h-6 text-white/40 mx-auto mb-2" />
          <p className="tc-empty-text">Vetrina vuota — i gift ricevuti diventano trofei</p>
        </div>
      )}
    </div>
  );
}

function TrophyHeader({ data }) {
  return (
    <div className="tc-head" data-testid="trophy-header">
      <div>
        <p className="tc-head-label">Vetrina trofei</p>
        <p className="tc-head-num">{data?.total_gifts || 0}</p>
      </div>
      <span className="tc-head-coins">
        <Coins className="w-3.5 h-3.5" />
        {formatCoins(data?.total_value_coins || 0)}
      </span>
    </div>
  );
}

export const ProfileGiftDetails = ({ userId, isOwner = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // rispetta le impostazioni privacy: se la vetrina non è visibile la sezione non compare
    fetchGifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchGifts = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/gifts/received/${userId}`);
      if (res.data.visible === false) setVisible(false);
      else setData(res.data.summary);
    } catch (e) { if (e.response?.status === 403) setVisible(false); }
    finally { setLoading(false); }
  };

  if (!visible) return null;
  if (!isOwner && !loading && (data?.total_gifts || 0) === 0) return null;

  return (
    <div className="tc-root" data-testid="profile-trophy-case">
      <button className="tc-toggle" onClick={() => setExpanded(!expanded)} data-testid="trophy-toggle">
        <Trophy className="w-4 h-4" style={{ color: "#FFD166" }} />
        <span className="tc-toggle-text">{isOwner ? "La tua vetrina trofei" : "Vetrina trofei"}</span>
        {expanded ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden">
            {loading ? (
              <div className="tc-skel" />
            ) : (
              <>
                <TrophyHeader data={data} />
                <TrophyShelf data={data} compact />
                {(data?.by_type || []).length > 0 && (
                  <button className="tc-all" onClick={() => setShowAll(true)} data-testid="trophy-see-all">
                    Vedi tutta la vetrina
                  </button>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <GiftDetailsModal userId={userId} open={showAll} onOpenChange={setShowAll} />
    </div>
  );
};

export const GiftDetailsModal = ({ userId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !data) {
      setLoading(true);
      api.get(`/gifts/received/${userId}`)
        .then((res) => { if (res.data.visible !== false) setData(res.data.summary); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tc-dialog max-w-md" data-testid="trophy-case-modal">
        <DialogHeader>
          <DialogTitle className="tc-dialog-title">Vetrina trofei</DialogTitle>
        </DialogHeader>
        {loading ? <div className="tc-skel" /> : (
          <>
            <TrophyHeader data={data} />
            {data?.most_received && (
              <div className="tc-spot" data-testid="trophy-most-received">
                <span className="tc-spot-rays" />
                <TrophyArt type={data.most_received.gift_type} url={data.most_received.gift_image} size={62} />
                <div className="relative">
                  <p className="tc-spot-name">{data.most_received.gift_name}</p>
                  <p className="tc-spot-meta">{data.most_received.count} {data.most_received.count === 1 ? "volta" : "volte"} · trofeo principale</p>
                </div>
              </div>
            )}
            <div className="max-h-[52vh] overflow-y-auto pr-1">
              <TrophyShelf data={data} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileGiftDetails;
